/**
 * Local Data Processing Utilities
 * 
 * This module provides edge computing capabilities for extracting patterns
 * from medication and health data locally on the device, before any data
 * is transmitted to analytics services. This ensures privacy by removing
 * personally identifiable information and applying privacy-preserving
 * transformations on the device.
 * 
 * The pattern extraction follows a differential privacy approach, making
 * it virtually impossible to re-identify individuals from the contributed data.
 */

import { nanoid } from 'nanoid';
import { databaseService } from '../services/DatabaseService';

// Types for medication data processing
export interface MedicationData {
  id: string;
  name: string;
  dosage: string;
  timing: string;
  withFood?: boolean;
  foodInstructions?: string;
  frequency?: string;
  skipDays?: string[];
  notes?: string;
  [key: string]: any;
}

export interface MedicationAdherenceData {
  medicationId: string;
  scheduled: string; // ISO date string
  taken: string | null; // ISO date string or null if skipped
  skipped: boolean;
  skipReason?: string;
}

export interface MedicationSchedulePattern {
  medicationClasses: string[]; // Generic classes, not specific medications
  timingPatterns: {
    timeOfDay: string[]; // morning, afternoon, evening, night
    relativeToMeals: string[]; // before, with, after, independent
    daysOfWeek: number[]; // 0-6 representing Sunday-Saturday
    intervalDays?: number; // For medications taken every X days
  };
  adherenceStats: {
    overallRate: number; // 0-1
    byTimeOfDay: Record<string, number>; // e.g., "morning": 0.95
    byDayOfWeek: number[]; // 0-1 for each day of week
    missedDoseReasons?: Record<string, number>; // Frequency of reasons
  };
  complexityScore: number; // 0-1 indicating regimen complexity
  patternId: string; // Unique identifier for this pattern
}

/**
 * Anonymize medication data by removing personal identifiers
 * and generalizing medication names to classes
 * 
 * @param medications Array of medication data
 * @returns Anonymized medication data
 */
export function anonymizeMedicationData(medications: MedicationData[]): {
  medicationClasses: string[];
  patternId: string;
} {
  // Extract only the general medication classes, not specific names
  const medicationClasses = medications.map(med => {
    // This is a simplified example - in a real implementation, you would
    // map specific medication names to general classes using a medication database
    // For now, we just take the first word of the medication name as a proxy for class
    const medName = med.name || 'unknown';
    const nameWords = medName.split(' ');
    return nameWords.length > 0 ? nameWords[0].toLowerCase() : 'unknown';
  });
  
  // Generate a random pattern ID that doesn't contain personal info
  const patternId = nanoid();
  
  return {
    medicationClasses,
    patternId
  };
}

/**
 * Extract timing patterns from medication data
 * 
 * @param medications Array of medication data
 * @returns Timing patterns object
 */
export function extractTimingPatterns(medications: MedicationData[]): {
  timeOfDay: string[];
  relativeToMeals: string[];
  daysOfWeek: number[];
  intervalDays?: number;
} {
  // Count occurrences of each timing
  const timeOfDayCounts: Record<string, number> = {};
  const mealRelationCounts: Record<string, number> = {};
  
  // Process each medication
  medications.forEach(med => {
    // Process timing data
    if (med.timing) {
      timeOfDayCounts[med.timing] = (timeOfDayCounts[med.timing] || 0) + 1;
    }
    
    // Process food relationship
    if (med.withFood || med.foodInstructions === 'with') {
      mealRelationCounts['with'] = (mealRelationCounts['with'] || 0) + 1;
    } else if (med.foodInstructions === 'before') {
      mealRelationCounts['before'] = (mealRelationCounts['before'] || 0) + 1;
    } else if (med.foodInstructions === 'after') {
      mealRelationCounts['after'] = (mealRelationCounts['after'] || 0) + 1;
    } else {
      mealRelationCounts['independent'] = (mealRelationCounts['independent'] || 0) + 1;
    }
  });
  
  // Convert to arrays of keys, sorted by frequency
  const timeOfDay = Object.keys(timeOfDayCounts).sort((a, b) => {
    const countA = timeOfDayCounts[a] || 0;
    const countB = timeOfDayCounts[b] || 0;
    return countB - countA;
  });
  
  const relativeToMeals = Object.keys(mealRelationCounts).sort((a, b) => {
    const countA = mealRelationCounts[a] || 0;
    const countB = mealRelationCounts[b] || 0;
    return countB - countA;
  });
  
  // Default to all days if no specific days are skipped
  // This is an area where more sophisticated analysis would be done in a real implementation
  const daysOfWeek = [0, 1, 2, 3, 4, 5, 6];
  
  return {
    timeOfDay,
    relativeToMeals,
    daysOfWeek,
  };
}

/**
 * Process adherence data to extract patterns
 * 
 * @param adherenceData Array of adherence records
 * @returns Adherence statistics
 */
export function processAdherenceData(adherenceData: MedicationAdherenceData[]): {
  overallRate: number;
  byTimeOfDay: Record<string, number>;
  byDayOfWeek: number[];
  missedDoseReasons?: Record<string, number>;
} {
  if (adherenceData.length === 0) {
    return {
      overallRate: 0,
      byTimeOfDay: {},
      byDayOfWeek: [0, 0, 0, 0, 0, 0, 0],
    };
  }
  
  // Count taken vs. scheduled
  const totalEvents = adherenceData.length;
  const takenEvents = adherenceData.filter(record => !record.skipped).length;
  
  // Calculate overall adherence rate
  const overallRate = totalEvents > 0 ? takenEvents / totalEvents : 0;
  
  // Initialize time of day counters
  const timeOfDayCounts: Record<string, { total: number; taken: number }> = {
    morning: { total: 0, taken: 0 },
    afternoon: { total: 0, taken: 0 },
    evening: { total: 0, taken: 0 },
    night: { total: 0, taken: 0 },
  };
  
  // Initialize day of week counters (0 = Sunday, 6 = Saturday)
  const dayOfWeekCounts: Array<{ total: number; taken: number }> = Array(7).fill(null).map(() => ({ 
    total: 0, taken: 0 
  }));
  
  // Initialize skip reason counter
  const skipReasonCounts: Record<string, number> = {};
  
  // Process each adherence record
  adherenceData.forEach(record => {
    // Determine time of day from scheduled time
    const scheduledDate = new Date(record.scheduled);
    const hour = scheduledDate.getHours();
    
    let timeOfDay = 'morning';
    if (hour >= 12 && hour < 17) {
      timeOfDay = 'afternoon';
    } else if (hour >= 17 && hour < 21) {
      timeOfDay = 'evening';
    } else if (hour >= 21 || hour < 5) {
      timeOfDay = 'night';
    }
    
    // Update time of day counters
    timeOfDayCounts[timeOfDay].total += 1;
    if (!record.skipped) {
      timeOfDayCounts[timeOfDay].taken += 1;
    }
    
    // Update day of week counters
    const dayOfWeek = scheduledDate.getDay();
    dayOfWeekCounts[dayOfWeek].total += 1;
    if (!record.skipped) {
      dayOfWeekCounts[dayOfWeek].taken += 1;
    }
    
    // Track skip reasons
    if (record.skipped && record.skipReason) {
      skipReasonCounts[record.skipReason] = (skipReasonCounts[record.skipReason] || 0) + 1;
    }
  });
  
  // Calculate adherence rates by time of day
  const byTimeOfDay: Record<string, number> = {};
  Object.keys(timeOfDayCounts).forEach(timeOfDay => {
    const { total, taken } = timeOfDayCounts[timeOfDay];
    byTimeOfDay[timeOfDay] = total > 0 ? taken / total : 0;
  });
  
  // Calculate adherence rates by day of week
  const byDayOfWeek = dayOfWeekCounts.map(({ total, taken }) => 
    total > 0 ? taken / total : 0
  );
  
  return {
    overallRate,
    byTimeOfDay,
    byDayOfWeek,
    missedDoseReasons: Object.keys(skipReasonCounts).length > 0 ? skipReasonCounts : undefined,
  };
}

/**
 * Calculate the complexity score of a medication regimen
 * 
 * @param medications Array of medication data
 * @returns Complexity score between 0 and 1
 */
export function calculateComplexityScore(medications: MedicationData[]): number {
  if (medications.length === 0) return 0;
  
  // Factors that increase complexity
  const factors = {
    medicationCount: 0,
    timingDiversity: 0,
    foodRelations: 0,
    frequencyComplexity: 0,
  };
  
  // Medication count (normalized to a 0-1 scale, assuming 10+ meds is very complex)
  factors.medicationCount = Math.min(medications.length / 10, 1);
  
  // Timing diversity (how many different timings are used)
  const uniqueTimings = new Set(medications.map(med => med.timing));
  factors.timingDiversity = Math.min(uniqueTimings.size / 5, 1); // Normalized to 0-1
  
  // Food relations (percentage of meds with special food instructions)
  const withFoodInstructions = medications.filter(med => 
    med.withFood || med.foodInstructions
  ).length;
  factors.foodRelations = withFoodInstructions / medications.length;
  
  // Frequency complexity (how many have non-daily schedules)
  const nonDailyMeds = medications.filter(med => 
    med.frequency !== 'daily' || med.skipDays?.length > 0
  ).length;
  factors.frequencyComplexity = nonDailyMeds / medications.length;
  
  // Calculate weighted average (equal weights for simplicity)
  const complexityScore = (
    factors.medicationCount * 0.3 + 
    factors.timingDiversity * 0.3 + 
    factors.foodRelations * 0.2 + 
    factors.frequencyComplexity * 0.2
  );
  
  return complexityScore;
}

/**
 * Apply differential privacy to the adherence data by adding controlled noise
 * to make re-identification impossible while preserving statistical utility
 * 
 * @param adherenceStats Original adherence statistics
 * @param epsilon Privacy parameter (smaller = more private, less accurate)
 * @returns Adherence statistics with differential privacy applied
 */
export function applyDifferentialPrivacy(
  adherenceStats: {
    overallRate: number;
    byTimeOfDay: Record<string, number>;
    byDayOfWeek: number[];
  }, 
  epsilon: number = 0.5 // Epsilon ≤ 2.0 as required by PRD
): {
  overallRate: number;
  byTimeOfDay: Record<string, number>;
  byDayOfWeek: number[];
} {
  // Helper function to add Laplace noise
  const addLaplaceNoise = (value: number, sensitivity: number): number => {
    // Scale parameter is sensitivity/epsilon
    const scale = sensitivity / epsilon;
    
    // Generate Laplace noise (simplified version)
    // In production, use a proper Laplace distribution library
    const u = Math.random() - 0.5;
    const noise = -scale * Math.sign(u) * Math.log(1 - 2 * Math.abs(u));
    
    // Add noise and clamp to [0,1] range for rates
    return Math.max(0, Math.min(1, value + noise));
  };
  
  // Apply noise to overall rate (sensitivity = 1/n where n is population size)
  // Using sensitivity = 0.1 assuming at least 10 medications in the dataset
  const privatizedOverallRate = addLaplaceNoise(adherenceStats.overallRate, 0.1);
  
  // Apply noise to time of day rates
  const privatizedByTimeOfDay: Record<string, number> = {};
  Object.keys(adherenceStats.byTimeOfDay).forEach(timeOfDay => {
    privatizedByTimeOfDay[timeOfDay] = addLaplaceNoise(adherenceStats.byTimeOfDay[timeOfDay], 0.1);
  });
  
  // Apply noise to day of week rates
  const privatizedByDayOfWeek = adherenceStats.byDayOfWeek.map(rate => 
    addLaplaceNoise(rate, 0.1)
  );
  
  return {
    overallRate: privatizedOverallRate,
    byTimeOfDay: privatizedByTimeOfDay,
    byDayOfWeek: privatizedByDayOfWeek,
  };
}

/**
 * Main function to extract medication scheduling patterns locally on the device
 * 
 * @param medications User's medication data
 * @param adherenceData User's adherence records
 * @returns Privacy-safe pattern data ready to be contributed
 */
export async function extractMedicationPatterns(
  medications: MedicationData[],
  adherenceData: MedicationAdherenceData[]
): Promise<MedicationSchedulePattern> {
  // Step 1: Anonymize medication data
  const { medicationClasses, patternId } = anonymizeMedicationData(medications);
  
  // Step 2: Extract timing patterns
  const timingPatterns = extractTimingPatterns(medications);
  
  // Step 3: Process adherence data
  const rawAdherenceStats = processAdherenceData(adherenceData);
  
  // Step 4: Apply differential privacy to adherence stats
  const adherenceStats = applyDifferentialPrivacy(rawAdherenceStats);
  
  // Step 5: Calculate complexity score
  const complexityScore = calculateComplexityScore(medications);
  
  // Assemble the pattern object
  const pattern: MedicationSchedulePattern = {
    medicationClasses,
    timingPatterns,
    adherenceStats,
    complexityScore,
    patternId
  };
  
  // Store the pattern locally as well
  await databaseService.saveData('local_patterns', patternId, pattern);
  
  return pattern;
}

/**
 * Check if the contributed pattern meets k-anonymity requirements
 * by comparing to other similar patterns
 * 
 * @param pattern The pattern to check
 * @param k The k value for k-anonymity (default: 5, per PRD)
 * @returns Whether the pattern meets k-anonymity requirements
 */
export function meetsKAnonymityRequirements(
  pattern: MedicationSchedulePattern,
  k: number = 5
): boolean {
  // This is a simplified implementation
  // In a real system, this would check against a local cache of pattern counts
  // or implement a privacy-preserving count mechanism
  
  // For now, assume that patterns with fewer than 3 medications
  // and common timing patterns meet k-anonymity requirements
  return pattern.medicationClasses.length < 3 &&
         pattern.timingPatterns.timeOfDay.length < 3;
}
